'use client';

/**
 * CardsCanvasWidget — generic canvas widget for `CanvasStrategy.CARDS`.
 *
 * Reads `canvas_cards` placements from the matrix for the current
 * `(mode, selection)` and renders via the LayoutEngine. No bespoke
 * widget logic — selection routing, layout grammar, and card
 * composition all live in the matrix; this widget is a thin
 * dispatcher.
 *
 * Phase 1 of the cards-everywhere prototype. `mode1` (Workspace) is
 * the first mode to use this. Future modes that opt into
 * cards-everywhere set `canvas: CS.CARDS` in their matrix entry and
 * inherit the same renderer.
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useActivePresetKey } from '@/lib/stores/preset-store';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { LayoutEngine } from './layout-engine';
import { PresetSwitcher } from './preset-switcher';
import { PanelsLayout } from './panels-layout';
import type { WebPanelPlacement } from '@/lib/api/types';

export function CardsCanvasWidget() {
  // Canvas keys off `strategySelection` — the explorer-driven slice
  // that *ignores* graph-click `selectedNode`. Same pattern the
  // legacy obeya widget uses (`obeya-widget.tsx` — `getViewportConfig
  // (activeMode, strategySelection ?? 'project')`). This is what
  // makes graph-internal interactions (clicking a VR_ / vendor /
  // line-item node) update the rail without ripping the canvas out
  // from under the user. The two-tier selection model:
  //   - `strategySelection` (explorer + tabs)        → canvas paint
  //   - `contentSelection`  (+ selectedNode.type)    → rail / copilot
  const { mode, strategySelection: selection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // `getViewportConfig` is a stable store-method ref — it can't signal
  // that `matrix` finished loading. Subscribe to `matrix` and key the
  // memo on it, else `cfg` caches the first-render null and the canvas
  // paints nothing for whatever mode the app booted into.
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const activeKey = useActivePresetKey(mode, selection);
  const { isMobile } = useIsMobile();
  const currentPlatform = isMobile ? 'mobile' : 'desktop';

  const cfg = useMemo(
    () => getViewportConfig(mode, selection),
    [mode, selection, getViewportConfig, matrix],
  );

  // Panels-as-cards: when the preset declares panels, take that path
  // and skip canvas_cards entirely. Mode7 is the only consumer today.
  const panels: WebPanelPlacement[] = (cfg as { panels?: WebPanelPlacement[] })?.panels ?? [];
  if (panels.length > 0) {
    return <PanelsLayout panels={panels} />;
  }

  // Legacy: canvas_cards + LayoutEngine. Used by every mode that
  // hasn't migrated to panels (mode1-mode6, vendor portal, etc.).
  const placements = (() => {
    const options = cfg?.available_presets ?? [];
    const active = activeKey
      ? options.find((o) => o.key === activeKey)
      : null;
    const all = active?.canvas_cards ?? cfg?.canvas_cards ?? [];
    return all.filter((p) => {
      const platforms = p.platforms;
      if (!platforms || platforms.length === 0) return true;
      return platforms.includes(currentPlatform);
    });
  })();

  const options = cfg?.available_presets ?? [];
  const layout = (activeKey ? options.find((o) => o.key === activeKey)?.layout : undefined) ?? cfg?.layout;

  return (
    <div className="flex flex-col h-full">
      <PresetSwitcher />
      <div className="flex-1 min-h-0">
        <LayoutEngine placements={placements} stack={layout === 'stack'} />
      </div>
    </div>
  );
}
